import React, { useEffect, useState } from 'react';
import { industrialProductsAPI, supplierProductsAPI, suppliersAPI, priceHistoryAPI } from '@/lib/api';
import PriceSparkline from '@/components/PriceSparkline';
import ProductPhotoAssistantDialog from '@/components/ProductPhotoAssistantDialog';
import CategorizedProductSelectContent from '@/pages/orders/components/CategorizedProductSelectContent';
import { formatCurrency, matchesSearch } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
    Package, 
    Plus, 
    Pencil, 
    Sparkles,
    Trash2, 
    Search,
    ChevronDown,
    ChevronRight,
    Link as LinkIcon,
    Filter,
    Building2,
    PlusCircle,
    Camera,
    X,
    ImageIcon,
    Loader2,
    Eye,
    EyeOff,
    ShoppingCart,
    ArrowRightLeft,
    BarChart3,
    Scale
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import ImageCropDialog from '@/components/ImageCropDialog';
import MoveSupplierProductDialog from '@/components/MoveSupplierProductDialog';
import ProductsDialogs from './products/ProductsDialogs';
import { EU_ALLERGENS as ALLERGENS_CONST, FOOD_FAMILIES as FOOD_FAMILIES_CONST, isFoodFamily as isFoodFamilyFn } from './products/productsConstants';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

// Default unit lists (base units that can be restored)
import { BASE_ORDER_UNITS, BASE_BILLING_UNITS, loadUnits, saveCustomUnit, removeUnit } from '@/lib/units';

export default function ProductsPage() {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const canCreate = hasPermission('products_create');
    const canEdit = hasPermission('products_edit');
    const canDelete = hasPermission('products_delete');
    
    // Permissions pour les produits fournisseurs
    const canCreateSupplierProduct = hasPermission('supplier_products_create');
    const canEditSupplierProduct = hasPermission('supplier_products_edit');
    const canDeleteSupplierProduct = hasPermission('supplier_products_delete');
    const canToggleCommandable = hasPermission('supplier_products_commandable');
    
    const [products, setProducts] = useState([]);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [priceSparklines, setPriceSparklines] = useState({});
    const [suppliers, setSuppliers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [familles, setFamilles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtersVisible, setFiltersVisible] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterFamille, setFilterFamille] = useState('all');
    const [filterSupplier, setFilterSupplier] = useState('all');
    const [filterRecency, setFilterRecency] = useState('all'); // 'all' | 'week' | 'month'
    const [groupBy, setGroupBy] = useState('category');
    const [expandedProduct, setExpandedProduct] = useState(null);

    // Mobile back-button: close the expanded product card instead of leaving the page.
    // When a product is expanded, push a dummy history state. The next "popstate"
    // (= mobile back) will trigger here and we just close the card without navigating.
    // When the user closes it manually (click), we go history.back() to keep the
    // history stack clean.
    useEffect(() => {
        if (expandedProduct) {
            window.history.pushState({ orderSyncProductExpanded: true }, '');
            const onPop = () => {
                // Close the card; do NOT navigate.
                setExpandedProduct(null);
            };
            window.addEventListener('popstate', onPop);
            return () => window.removeEventListener('popstate', onPop);
        }
    }, [expandedProduct]);

    const closeExpandedProduct = () => {
        // Trigger popstate so the dummy history entry is removed cleanly.
        if (window.history.state?.orderSyncProductExpanded) {
            window.history.back();
        } else {
            setExpandedProduct(null);
        }
    };
    const [editMode, setEditMode] = useState(false);
    const [photoAssistantOpen, setPhotoAssistantOpen] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [selectedSupplierProducts, setSelectedSupplierProducts] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null);
    const [bulkValue, setBulkValue] = useState('');

    const EU_ALLERGENS = ALLERGENS_CONST;
    const FOOD_FAMILIES = FOOD_FAMILIES_CONST;
    const isFoodFamily = isFoodFamilyFn;
    const [hideUnselectedAllergens, setHideUnselectedAllergens] = useState(true);
    
    // Industrial Product Dialog
    const [productDialog, setProductDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productForm, setProductForm] = useState({
        name: '', description: '', unit: 'Pièce', category: '', famille: '', allergens: [], vat_rate: 2.1,
        fuji_express: true, concept_lab: true, my_food: true, boucherie: true
    });
    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showNewFamilleInput, setShowNewFamilleInput] = useState(false);
    const [newFamilleName, setNewFamilleName] = useState('');
    const [famillePopoverOpen, setFamillePopoverOpen] = useState(false);
    
    // Product Image
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imageDeleted, setImageDeleted] = useState(false);
    const [imageSourceRef, setImageSourceRef] = useState(null); // { sourceType, sourceId } from picker
    const [showCropDialog, setShowCropDialog] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [showImagePicker, setShowImagePicker] = useState(false); // for industrial product dialog
    
    // Supplier Product Detail Popup
    const [spDetailProduct, setSpDetailProduct] = useState(null);

    // Supplier Product Dialog
    const [supplierProductDialog, setSupplierProductDialog] = useState(false);
    const [editingSupplierProduct, setEditingSupplierProduct] = useState(null);
    // Supplier Product Image
    const [spImagePreview, setSpImagePreview] = useState(null);
    const [spImageFile, setSpImageFile] = useState(null);
    const [spImageDeleted, setSpImageDeleted] = useState(false);
    const [spImageSourceRef, setSpImageSourceRef] = useState(null); // { sourceType, sourceId } from picker
    const [spShowCropDialog, setSpShowCropDialog] = useState(false);
    const [spCropImageSrc, setSpCropImageSrc] = useState(null);
    const [spImageHeight, setSpImageHeight] = useState('');
    const [spLoadingImage, setSpLoadingImage] = useState(false);
    const [showSpImagePicker, setShowSpImagePicker] = useState(false);
    // Move supplier product to another industrial product
    const [moveSpDialog, setMoveSpDialog] = useState({ open: false, supplierProduct: null, currentName: '' });
    const [supplierProductForm, setSupplierProductForm] = useState({
        industrial_product_id: '',
        supplier_id: '',
        supplier_ref: '',
        supplier_name_product: '',
        brand: '',
        unit_price: '',
        packaging: '',
        capacity_value: '',
        capacity_unit: '',
        order_unit: 'Pièce',
        billing_unit: 'Pièce',
        invoice_unit: 'Pièce',
        packaging_qty: '',
        sell_by_detail: false,
        variable_weight: false,
        unknown_packaging: false,  // Colisage inconnu - seule l'unité de facturation sera utilisée
        min_order_qty: 1,
        lead_time_days: 0,
        commandable: true
    });
    
    // Delete Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteType, setDeleteType] = useState('product');
    
    // Custom units management
    const [orderUnits, setOrderUnits] = useState(() => loadUnits('customOrderUnits', 'deletedOrderUnits', BASE_ORDER_UNITS));
    const [billingUnits, setBillingUnits] = useState(() => loadUnits('customBillingUnits', 'deletedBillingUnits', BASE_BILLING_UNITS));
    const [invoiceUnits, setInvoiceUnits] = useState(() => loadUnits('customInvoiceUnits', 'deletedInvoiceUnits', BASE_BILLING_UNITS));
    const [newOrderUnit, setNewOrderUnit] = useState('');
    const [newBillingUnit, setNewBillingUnit] = useState('');
    const [newInvoiceUnit, setNewInvoiceUnit] = useState('');
    const [orderUnitPopoverOpen, setOrderUnitPopoverOpen] = useState(false);
    const [billingUnitPopoverOpen, setBillingUnitPopoverOpen] = useState(false);
    const [invoiceUnitPopoverOpen, setInvoiceUnitPopoverOpen] = useState(false);
    
    const handleAddOrderUnit = () => {
        if (newOrderUnit.trim() && !orderUnits.includes(newOrderUnit.trim())) {
            const unit = newOrderUnit.trim();
            setOrderUnits(prev => [...prev, unit]);
            saveCustomUnit('customOrderUnits', 'deletedOrderUnits', unit);
            setSupplierProductForm(prev => ({ ...prev, order_unit: unit }));
            setNewOrderUnit('');
            setOrderUnitPopoverOpen(false);
            toast.success(`Unité "${unit}" ajoutée`);
        }
    };
    
    const handleAddBillingUnit = () => {
        if (newBillingUnit.trim() && !billingUnits.includes(newBillingUnit.trim())) {
            const unit = newBillingUnit.trim();
            setBillingUnits(prev => [...prev, unit]);
            saveCustomUnit('customBillingUnits', 'deletedBillingUnits', unit);
            setSupplierProductForm(prev => ({ ...prev, billing_unit: unit }));
            setNewBillingUnit('');
            setBillingUnitPopoverOpen(false);
            toast.success(`Unité "${unit}" ajoutée`);
        }
    };

    const handleRemoveOrderUnit = (unit) => {
        // Prevent deleting if only one unit left
        if (orderUnits.length <= 1) {
            toast.error('Impossible de supprimer la dernière unité');
            return;
        }
        if (removeUnit('customOrderUnits', 'deletedOrderUnits', unit, BASE_ORDER_UNITS)) {
            setOrderUnits(prev => prev.filter(u => u !== unit));
            // Reset selection if the deleted unit was selected
            if (supplierProductForm.order_unit === unit) {
                const remaining = orderUnits.filter(u => u !== unit);
                setSupplierProductForm(prev => ({ ...prev, order_unit: remaining[0] || 'Pièce' }));
            }
            toast.success(`Unité "${unit}" supprimée`);
        }
    };

    const handleRemoveBillingUnit = (unit) => {
        // Prevent deleting if only one unit left
        if (billingUnits.length <= 1) {
            toast.error('Impossible de supprimer la dernière unité');
            return;
        }
        if (removeUnit('customBillingUnits', 'deletedBillingUnits', unit, BASE_BILLING_UNITS)) {
            setBillingUnits(prev => prev.filter(u => u !== unit));
            // Reset selection if the deleted unit was selected
            if (supplierProductForm.billing_unit === unit) {
                const remaining = billingUnits.filter(u => u !== unit);
                setSupplierProductForm(prev => ({ ...prev, billing_unit: remaining[0] || 'Pièce' }));
            }
            toast.success(`Unité "${unit}" supprimée`);
        }
    };

    const handleAddInvoiceUnit = () => {
        if (newInvoiceUnit.trim() && !invoiceUnits.includes(newInvoiceUnit.trim())) {
            const unit = newInvoiceUnit.trim();
            setInvoiceUnits(prev => [...prev, unit]);
            saveCustomUnit('customInvoiceUnits', 'deletedInvoiceUnits', unit);
            setSupplierProductForm(prev => ({ ...prev, invoice_unit: unit }));
            setNewInvoiceUnit('');
            setInvoiceUnitPopoverOpen(false);
            toast.success(`Unité "${unit}" ajoutée`);
        }
    };

    const handleRemoveInvoiceUnit = (unit) => {
        if (invoiceUnits.length <= 1) {
            toast.error('Impossible de supprimer la dernière unité');
            return;
        }
        if (removeUnit('customInvoiceUnits', 'deletedInvoiceUnits', unit, BASE_BILLING_UNITS)) {
            setInvoiceUnits(prev => prev.filter(u => u !== unit));
            if (supplierProductForm.invoice_unit === unit) {
                const remaining = invoiceUnits.filter(u => u !== unit);
                setSupplierProductForm(prev => ({ ...prev, invoice_unit: remaining[0] || 'Pièce' }));
            }
            toast.success(`Unité "${unit}" supprimée`);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadProducts();
    }, [filterCategory, filterFamille]);

    const loadData = async () => {
        try {
            const [productsRes, supplierProductsRes, suppliersRes, categoriesRes, famillesRes] = await Promise.all([
                industrialProductsAPI.getAll(),
                supplierProductsAPI.getAll(),
                suppliersAPI.getAll(),
                industrialProductsAPI.getCategories(),
                industrialProductsAPI.getFamilles()
            ]);
            setProducts(productsRes.data);
            setSupplierProducts(supplierProductsRes.data);
            setSuppliers(suppliersRes.data);
            setCategories(categoriesRes.data);
            setFamilles(famillesRes.data);

            // Load price history sparklines in bulk (fire-and-forget)
            const spIds = (supplierProductsRes.data || []).map(sp => sp.id).filter(Boolean);
            if (spIds.length > 0) {
                priceHistoryAPI.getSparklines(spIds, 365)
                    .then(res => setPriceSparklines(res.data || {}))
                    .catch(() => {});
            }
            
            // Synchronize units from existing supplier products
            const existingOrderUnits = new Set(orderUnits);
            const existingBillingUnits = new Set(billingUnits);
            const existingInvoiceUnits = new Set(invoiceUnits);
            
            supplierProductsRes.data.forEach(sp => {
                if (sp.order_unit && !existingOrderUnits.has(sp.order_unit)) {
                    existingOrderUnits.add(sp.order_unit);
                    saveCustomUnit('customOrderUnits', 'deletedOrderUnits', sp.order_unit);
                }
                if (sp.billing_unit && !existingBillingUnits.has(sp.billing_unit)) {
                    existingBillingUnits.add(sp.billing_unit);
                    saveCustomUnit('customBillingUnits', 'deletedBillingUnits', sp.billing_unit);
                }
                if (sp.invoice_unit && !existingInvoiceUnits.has(sp.invoice_unit)) {
                    existingInvoiceUnits.add(sp.invoice_unit);
                    saveCustomUnit('customInvoiceUnits', 'deletedInvoiceUnits', sp.invoice_unit);
                }
            });
            
            setOrderUnits([...existingOrderUnits]);
            setBillingUnits([...existingBillingUnits]);
            setInvoiceUnits([...existingInvoiceUnits]);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await industrialProductsAPI.getAll(
                filterCategory === 'all' ? null : filterCategory,
                (filterFamille === 'all' || filterFamille === '__none__') ? null : filterFamille
            );
            setProducts(response.data);
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    // Filter and sort products by category, famille and supplier
    const recencyThreshold = (() => {
        if (filterRecency === 'all') return null;
        const d = new Date();
        d.setDate(d.getDate() - (filterRecency === 'week' ? 7 : 30));
        return d.toISOString();
    })();

    const filteredProducts = products
        .filter(p => {
            // Get supplier products for this industrial product
            const productSupplierProducts = supplierProducts.filter(sp => sp.industrial_product_id === p.id);
            
            // Search in industrial product fields
            const matchesIndustrialSearch = matchesSearch(p.name, searchTerm) ||
                matchesSearch(p.category, searchTerm) ||
                matchesSearch(p.famille, searchTerm);
            
            // Search in supplier product fields (name, ref, brand, supplier name)
            const matchesSupplierProductSearch = productSupplierProducts.some(sp => 
                matchesSearch(sp.supplier_name_product, searchTerm) ||
                matchesSearch(sp.supplier_ref, searchTerm) ||
                matchesSearch(sp.brand, searchTerm) ||
                matchesSearch(suppliers.find(s => s.id === sp.supplier_id)?.name, searchTerm)
            );
            
            const matchesSearchTerm = matchesIndustrialSearch || matchesSupplierProductSearch;
            
            const matchesCategoryFilter = filterCategory === 'all' || p.category === filterCategory;
            const matchesFamilleFilter = filterFamille === 'all' || 
                (filterFamille === '__none__' ? (!p.famille || p.famille === '') : p.famille === filterFamille);
            
            // Filter by supplier: check if any supplier product matches
            const matchesSupplierFilter = filterSupplier === 'all' || 
                productSupplierProducts.some(sp => sp.supplier_id === filterSupplier);

            // Filter by recency: industrial product OR any linked supplier product created after threshold
            const matchesRecency = !recencyThreshold
                || (p.created_at && p.created_at >= recencyThreshold)
                || productSupplierProducts.some(sp => sp.created_at && sp.created_at >= recencyThreshold);

            return matchesSearchTerm && matchesCategoryFilter && matchesFamilleFilter && matchesSupplierFilter && matchesRecency;
        })
        .sort((a, b) => {
            // Primary group / secondary group depending on groupBy
            const primA = (groupBy === 'famille' ? a.famille : a.category) || '';
            const primB = (groupBy === 'famille' ? b.famille : b.category) || '';
            if (primA !== primB) {
                return primA.localeCompare(primB, 'fr');
            }
            const secA = (groupBy === 'famille' ? a.category : a.famille) || '';
            const secB = (groupBy === 'famille' ? b.category : b.famille) || '';
            if (secA !== secB) {
                return secA.localeCompare(secB, 'fr');
            }
            return a.name.localeCompare(b.name, 'fr');
        });

    const getSupplierProductsForProduct = (productId) => {
        return supplierProducts.filter(sp => sp.industrial_product_id === productId);
    };

    // Industrial Product handlers
    const handleOpenProductDialog = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setProductForm({
                name: product.name,
                description: product.description || '',
                unit: product.unit,
                category: product.category || '',
                famille: product.famille || '',
                allergens: product.allergens || [],
                vat_rate: product.vat_rate ?? 2.1,
                fuji_express: product.fuji_express !== false,
                concept_lab: product.concept_lab !== false,
                my_food: product.my_food !== false,
                boucherie: product.boucherie !== false,
            });
            setImagePreview(product.image_url ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : null);
        } else {
            setEditingProduct(null);
            setProductForm({ name: '', description: '', unit: 'Pièce', category: '', famille: '', allergens: [], vat_rate: 2.1, fuji_express: true, concept_lab: true, my_food: true, boucherie: true });
            setImagePreview(null);
        }
        setImageFile(null);
        setImageDeleted(false);
        setImageSourceRef(null);
        setShowNewCategoryInput(false);
        setNewCategoryName('');
        setShowNewFamilleInput(false);
        setNewFamilleName('');
        setFamillePopoverOpen(false);
        setProductDialog(true);
    };

    const handleSaveProduct = async () => {
        if (!productForm.name) {
            toast.error('Le nom est requis');
            return;
        }
        
        try {
            let productId;
            if (editingProduct) {
                await industrialProductsAPI.update(editingProduct.id, productForm);
                productId = editingProduct.id;
                toast.success('Produit mis à jour');
            } else {
                const res = await industrialProductsAPI.create(productForm);
                productId = res.data.id;
                toast.success('Produit créé');
            }
            // Handle image upload/delete
            if (imageFile && productId) {
                await industrialProductsAPI.uploadImage(productId, imageFile);
            } else if (imageSourceRef && productId) {
                await industrialProductsAPI.copyImage(productId, imageSourceRef.sourceType, imageSourceRef.sourceId);
            } else if (imageDeleted && productId) {
                await industrialProductsAPI.deleteImage(productId);
            }
            setProductDialog(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const handleToggleCommandable = async (sp) => {
        try {
            const next = !(sp.commandable !== false);
            await supplierProductsAPI.toggleCommandable(sp.id, next);
            toast.success(next ? 'Produit commandable' : 'Produit non commandable');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
        }
    };

    // Compare toggle (Page Produits → Comparatif). The selection is persisted
    // server-side so it's shared across users — a team can build the
    // comparison together. Toast count keeps the user aware of the cart size.
    const handleToggleCompare = async (sp) => {
        try {
            const next = !sp.compare;
            await supplierProductsAPI.toggleCompare(sp.id, next);
            toast.success(next ? 'Ajouté au comparatif' : 'Retiré du comparatif');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement');
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setCropImageSrc(reader.result);
            setShowCropDialog(true);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropDone = (file, previewUrl) => {
        setImageFile(file);
        setImagePreview(previewUrl);
        setImageDeleted(false);
        setImageSourceRef(null);
        setShowCropDialog(false);
    };

    const handleImageRemove = () => {
        setImageFile(null);
        setImagePreview(null);
        setImageDeleted(true);
        setImageSourceRef(null);
    };

    const handlePickExistingImage = ({ sourceType, sourceId, imageUrl }) => {
        setImageFile(null);
        setImageDeleted(false);
        setImageSourceRef({ sourceType, sourceId });
        setImagePreview(imageUrl);
        setShowImagePicker(false);
    };

    const handleSpPickExistingImage = ({ sourceType, sourceId, imageUrl }) => {
        setSpImageFile(null);
        setSpImageDeleted(false);
        setSpImageSourceRef({ sourceType, sourceId });
        setSpImagePreview(imageUrl);
        setShowSpImagePicker(false);
    };

    const toggleProductSelection = (productId, e) => {
        e.stopPropagation();
        setSelectedProducts(prev => {
            const next = new Set(prev);
            if (next.has(productId)) next.delete(productId);
            else next.add(productId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedProducts.size === filteredProducts.length) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
        }
    };

    const handleBulkApply = async () => {
        if (!bulkAction || !bulkValue) return;
        try {
            if (bulkAction === 'supplier_id') {
                if (selectedSupplierProducts.size === 0) return;
                await supplierProductsAPI.bulkUpdate(
                    Array.from(selectedSupplierProducts),
                    { supplier_id: bulkValue }
                );
                toast.success(`${selectedSupplierProducts.size} produit(s) fournisseur réassigné(s)`);
            } else if (bulkAction === 'industrial_product_id') {
                if (selectedSupplierProducts.size === 0) return;
                await supplierProductsAPI.bulkUpdate(
                    Array.from(selectedSupplierProducts),
                    { industrial_product_id: bulkValue }
                );
                toast.success(`${selectedSupplierProducts.size} produit(s) fournisseur rattaché(s) au nouveau produit industriel`);
                setSelectedSupplierProducts(new Set());
            } else {
                if (selectedProducts.size === 0) return;
                await industrialProductsAPI.bulkUpdate(
                    Array.from(selectedProducts),
                    { [bulkAction]: bulkValue }
                );
                toast.success(`${selectedProducts.size} produit(s) mis à jour`);
                setSelectedProducts(new Set());
            }
            setBulkAction(null);
            setBulkValue('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const toggleSupplierProductSelection = (spId, e) => {
        if (e) e.stopPropagation();
        setSelectedSupplierProducts(prev => {
            const next = new Set(prev);
            if (next.has(spId)) next.delete(spId); else next.add(spId);
            return next;
        });
    };

    // Supplier Product handlers
    const handleOpenSupplierProductDialog = (product, supplierProduct = null) => {
        if (supplierProduct) {
            setEditingSupplierProduct(supplierProduct);
            setSpImagePreview(supplierProduct.image_url ? `${process.env.REACT_APP_BACKEND_URL}${supplierProduct.image_url}` : null);
            setSpImageHeight(supplierProduct.image_height ? String(supplierProduct.image_height) : '');
            
            // Ensure the product's units are in the lists (add them if missing and save to localStorage)
            const prodOrderUnit = supplierProduct.order_unit || 'Pièce';
            const prodBillingUnit = supplierProduct.billing_unit || 'Pièce';
            const prodInvoiceUnit = supplierProduct.invoice_unit || supplierProduct.billing_unit || 'Pièce';
            
            if (prodOrderUnit && !orderUnits.includes(prodOrderUnit)) {
                setOrderUnits(prev => [...prev, prodOrderUnit]);
                saveCustomUnit('customOrderUnits', 'deletedOrderUnits', prodOrderUnit);
            }
            if (prodBillingUnit && !billingUnits.includes(prodBillingUnit)) {
                setBillingUnits(prev => [...prev, prodBillingUnit]);
                saveCustomUnit('customBillingUnits', 'deletedBillingUnits', prodBillingUnit);
            }
            if (prodInvoiceUnit && !invoiceUnits.includes(prodInvoiceUnit)) {
                setInvoiceUnits(prev => [...prev, prodInvoiceUnit]);
                saveCustomUnit('customInvoiceUnits', 'deletedInvoiceUnits', prodInvoiceUnit);
            }
            
            setSupplierProductForm({
                industrial_product_id: supplierProduct.industrial_product_id,
                supplier_id: supplierProduct.supplier_id,
                supplier_ref: supplierProduct.supplier_ref,
                supplier_name_product: supplierProduct.supplier_name_product,
                brand: supplierProduct.brand || '',
                unit_price: supplierProduct.unit_price.toString(),
                packaging: supplierProduct.packaging || '',
                capacity_value: supplierProduct.capacity_value?.toString() || '',
                capacity_unit: supplierProduct.capacity_unit || '',
                order_unit: prodOrderUnit,
                billing_unit: prodBillingUnit,
                invoice_unit: prodInvoiceUnit,
                packaging_qty: supplierProduct.packaging_qty?.toString() || '',
                sell_by_detail: supplierProduct.sell_by_detail || false,
                variable_weight: supplierProduct.variable_weight || false,
                unknown_packaging: supplierProduct.unknown_packaging || false,
                min_order_qty: supplierProduct.min_order_qty,
                lead_time_days: supplierProduct.lead_time_days,
                allergens: supplierProduct.allergens,
                commandable: supplierProduct.commandable !== false
            });
        } else {
            setEditingSupplierProduct(null);
            setSpImagePreview(null);
            setSpImageHeight('');
            setSupplierProductForm({
                industrial_product_id: product.id,
                supplier_id: '',
                supplier_ref: '',
                supplier_name_product: '',
                brand: '',
                unit_price: '',
                packaging: '',
                capacity_value: '',
                capacity_unit: '',
                order_unit: 'Pièce',
                billing_unit: 'Pièce',
                invoice_unit: 'Pièce',
                packaging_qty: '',
                sell_by_detail: false,
                variable_weight: false,
                unknown_packaging: false,
                min_order_qty: 1,
                lead_time_days: 0,
                allergens: null,
                commandable: true
            });
        }
        setSpImageFile(null);
        setSpImageDeleted(false);
        setSpImageSourceRef(null);
        setSupplierProductDialog(true);
    };
    
    // Calculate price per unit automatically
    const calculatePricePerUnit = () => {
        const price = parseFloat(supplierProductForm.unit_price);
        const capacity = parseFloat(supplierProductForm.capacity_value);
        if (price > 0 && capacity > 0) {
            return (price / capacity).toFixed(2);
        }
        return null;
    };

    const handleSaveSupplierProduct = async () => {
        if (!supplierProductForm.supplier_id || !supplierProductForm.supplier_ref || !supplierProductForm.unit_price) {
            toast.error('Fournisseur, référence et prix sont requis');
            return;
        }
        
        try {
            // Calculate price_per_unit automatically
            const pricePerUnit = calculatePricePerUnit();
            
            const data = {
                industrial_product_id: supplierProductForm.industrial_product_id,
                supplier_id: supplierProductForm.supplier_id,
                supplier_ref: supplierProductForm.supplier_ref,
                supplier_name_product: supplierProductForm.supplier_name_product,
                brand: supplierProductForm.brand || '',
                unit_price: parseFloat(supplierProductForm.unit_price),
                packaging: supplierProductForm.packaging,
                capacity_value: supplierProductForm.capacity_value ? parseFloat(supplierProductForm.capacity_value) : null,
                capacity_unit: supplierProductForm.capacity_unit,
                order_unit: supplierProductForm.unknown_packaging ? supplierProductForm.invoice_unit : (supplierProductForm.order_unit || 'Pièce'),
                billing_unit: supplierProductForm.unknown_packaging ? supplierProductForm.invoice_unit : (supplierProductForm.billing_unit || 'Pièce'),
                invoice_unit: supplierProductForm.invoice_unit || 'Pièce',
                packaging_qty: supplierProductForm.unknown_packaging ? null : (supplierProductForm.packaging_qty ? parseFloat(supplierProductForm.packaging_qty) : null),
                sell_by_detail: supplierProductForm.sell_by_detail || false,
                variable_weight: supplierProductForm.variable_weight || false,
                unknown_packaging: supplierProductForm.unknown_packaging || false,
                min_order_qty: supplierProductForm.min_order_qty,
                lead_time_days: supplierProductForm.lead_time_days,
                image_height: spImageHeight ? parseInt(spImageHeight, 10) : null,
                allergens: supplierProductForm.allergens,
                commandable: supplierProductForm.commandable !== false
            };
            
            if (editingSupplierProduct) {
                await supplierProductsAPI.update(editingSupplierProduct.id, data);
                // Handle image upload/copy/delete for existing product
                if (spImageFile) {
                    await supplierProductsAPI.uploadImage(editingSupplierProduct.id, spImageFile);
                } else if (spImageSourceRef) {
                    await supplierProductsAPI.copyImage(editingSupplierProduct.id, spImageSourceRef.sourceType, spImageSourceRef.sourceId);
                } else if (spImageDeleted) {
                    await supplierProductsAPI.deleteImage(editingSupplierProduct.id);
                }
                toast.success('Produit fournisseur mis à jour');
            } else {
                const res = await supplierProductsAPI.create(data);
                const newId = res.data?.id;
                // Handle image upload/copy for new product
                if (spImageFile && newId) {
                    await supplierProductsAPI.uploadImage(newId, spImageFile);
                } else if (spImageSourceRef && newId) {
                    await supplierProductsAPI.copyImage(newId, spImageSourceRef.sourceType, spImageSourceRef.sourceId);
                }
                toast.success('Produit fournisseur ajouté');
            }
            setSupplierProductDialog(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    // Delete handlers
    const handleDeleteClick = (item, type) => {
        setItemToDelete(item);
        setDeleteType(type);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            if (deleteType === 'product') {
                await industrialProductsAPI.delete(itemToDelete.id);
                toast.success('Produit supprimé');
            } else {
                await supplierProductsAPI.delete(itemToDelete.id);
                toast.success('Produit fournisseur supprimé');
            }
            setDeleteDialog(false);
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Fixed list of category shortcut buttons (ordered by frequency, validated by user)
    const CATEGORY_SHORTCUTS = [
        'Sec Alimentaire',
        'Froid Négatif',
        'Froid Positif',
        'Boissons N.A.',
        'Alcool',
        'Consommable',
        'Emballage',
        'Entretien',
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-7rem)] animate-fade-in" data-testid="products-page">
            {/* Compact header: row 1 = filters & actions, row 2 = category shortcuts */}
            <div className="flex-shrink-0 bg-white border border-slate-200 rounded-lg p-3 shadow-sm space-y-2">
                {/* Row 1: title + search + filters + actions */}
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg font-bold text-slate-900 mr-1">Produits</h1>
                    <div className="relative flex-1 min-w-[180px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8 h-8 text-sm"
                            data-testid="search-products-input"
                        />
                    </div>
                    <Button
                        variant={filtersVisible ? 'secondary' : 'outline'}
                        size="sm"
                        className="h-8 px-2.5"
                        onClick={() => setFiltersVisible(v => !v)}
                        title={filtersVisible ? 'Masquer les filtres et raccourcis' : 'Afficher les filtres et raccourcis'}
                        data-testid="toggle-filters-btn"
                    >
                        <Filter className="w-3.5 h-3.5" />
                        {filtersVisible ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronRight className="w-3 h-3 ml-1" />}
                    </Button>
                    {filtersVisible && (<>
                    <Select value={filterFamille} onValueChange={setFilterFamille}>
                        <SelectTrigger data-testid="filter-famille-select" className="w-[145px] h-8 text-sm">
                            <SelectValue placeholder="Toutes les familles" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes les familles</SelectItem>
                            <SelectItem value="__none__" className="text-slate-500 italic">Sans famille</SelectItem>
                            {familles.map((fam) => (
                                <SelectItem key={fam} value={fam}>{fam}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger data-testid="filter-category-select" className="w-[145px] h-8 text-sm">
                            <SelectValue placeholder="Toutes catégories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes les catégories</SelectItem>
                            {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                        <SelectTrigger data-testid="filter-supplier-select" className="w-[145px] h-8 text-sm">
                            <SelectValue placeholder="Tous fournisseurs" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les fournisseurs</SelectItem>
                            {[...suppliers].sort((a, b) => a.name.localeCompare(b.name, 'fr')).map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1.5">
                        <Label className="text-xs text-slate-400 whitespace-nowrap">Groupe</Label>
                        <Select value={groupBy} onValueChange={setGroupBy}>
                            <SelectTrigger className="w-[110px] h-8 text-sm" data-testid="group-by-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="category">Catégorie</SelectItem>
                                <SelectItem value="famille">Famille</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto flex items-center gap-2 justify-end flex-wrap">
                        <div className="flex items-center gap-1" data-testid="recency-shortcuts">
                            <Label className="text-xs text-slate-400 whitespace-nowrap mr-1">Nouveaux</Label>
                            {[
                                { key: 'week', label: '1 sem.' },
                                { key: 'month', label: '1 mois' },
                            ].map(({ key, label }) => {
                                const active = filterRecency === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setFilterRecency(active ? 'all' : key)}
                                        className={`px-2.5 h-8 rounded-md text-xs font-semibold transition-colors border ${
                                            active
                                                ? 'bg-emerald-500 text-white border-emerald-500'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                        }`}
                                        data-testid={`recency-shortcut-${key}`}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Comparatif button — visible to anyone with view rights.
                            The badge shows the current comparison set size so the
                            user knows whether the page will actually have content. */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/products/comparison')}
                            data-testid="open-comparison-btn"
                            title="Ouvrir le comparatif de produits"
                        >
                            <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                            Comparatif
                            {(() => {
                                const n = supplierProducts.filter(sp => sp.compare).length;
                                if (n === 0) return null;
                                return (
                                    <span
                                        className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold px-1 rounded-full bg-indigo-500 text-white"
                                        data-testid="comparison-count-badge"
                                    >
                                        {n}
                                    </span>
                                );
                            })()}
                        </Button>
                        {canCreate && (
                        <>
                            <Button
                                variant={editMode ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                    setEditMode(!editMode);
                                    if (editMode) { setSelectedProducts(new Set()); setBulkAction(null); setBulkValue(''); }
                                }}
                                data-testid="edit-mode-btn"
                            >
                                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                                {editMode ? 'Terminer' : 'Édition'}
                            </Button>
                            <Button size="sm" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => setPhotoAssistantOpen(true)} data-testid="photo-assistant-btn">
                                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                Assistant photo
                            </Button>
                            <Button size="sm" onClick={() => handleOpenProductDialog()} className="btn-primary" data-testid="add-product-btn">
                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                Nouveau produit
                            </Button>
                        </>
                        )}
                    </div>
                    </>)}
                </div>
                {/* Row 2: category shortcut buttons */}
                {filtersVisible && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="category-shortcuts">
                    <Label className="text-xs font-semibold text-slate-500 whitespace-nowrap mr-1">Catégorie&nbsp;:</Label>
                    {CATEGORY_SHORTCUTS.map((cat) => {
                        const active = filterCategory === cat;
                        return (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setFilterCategory(active ? 'all' : cat)}
                                className={`px-3 h-7 rounded-full text-xs font-semibold transition-colors border ${
                                    active
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`category-shortcut-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            >
                                {cat}
                            </button>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Bulk Actions Bar */}
            {editMode && (selectedProducts.size > 0 || selectedSupplierProducts.size > 0) && (
                <Card className="border-orange-200 bg-orange-50 flex-shrink-0 mt-4">
                    <CardContent className="py-3 px-4 flex items-center gap-4 flex-wrap">
                        <span className="text-sm font-medium text-orange-800" data-testid="bulk-selection-count">
                            {selectedProducts.size > 0 && `${selectedProducts.size} produit(s) industriel(s)`}
                            {selectedProducts.size > 0 && selectedSupplierProducts.size > 0 && ' · '}
                            {selectedSupplierProducts.size > 0 && `${selectedSupplierProducts.size} produit(s) fournisseur(s)`}
                            {' sélectionné(s)'}
                        </span>
                        <div className="flex items-center gap-2">
                            <Select value={bulkAction || ''} onValueChange={(v) => { setBulkAction(v); setBulkValue(''); }}>
                                <SelectTrigger className="h-8 w-[210px] bg-white" data-testid="bulk-action-select">
                                    <SelectValue placeholder="Action..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedProducts.size > 0 && <SelectItem value="category">Changer catégorie</SelectItem>}
                                    {selectedProducts.size > 0 && <SelectItem value="famille">Changer famille</SelectItem>}
                                    {selectedSupplierProducts.size > 0 && <SelectItem value="supplier_id">Changer fournisseur</SelectItem>}
                                    {selectedSupplierProducts.size > 0 && <SelectItem value="industrial_product_id">Changer produit industriel</SelectItem>}
                                </SelectContent>
                            </Select>
                            {bulkAction && bulkAction === 'industrial_product_id' && (
                                <Select value={bulkValue} onValueChange={setBulkValue}>
                                    <SelectTrigger className="h-8 w-[280px] bg-white" data-testid="bulk-value-select">
                                        <SelectValue placeholder="Produit industriel..." />
                                    </SelectTrigger>
                                    <CategorizedProductSelectContent
                                        products={products.map(p => ({ ...p, supplier_name_product: p.name, industrial_product_id: p.id }))}
                                        industrialProducts={products}
                                    />
                                </Select>
                            )}
                            {bulkAction && bulkAction !== 'industrial_product_id' && (
                                <Select value={bulkValue} onValueChange={setBulkValue}>
                                    <SelectTrigger className="h-8 w-[220px] bg-white" data-testid="bulk-value-select">
                                        <SelectValue placeholder={
                                            bulkAction === 'category' ? 'Catégorie...' :
                                            bulkAction === 'famille' ? 'Famille...' :
                                            'Fournisseur...'
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bulkAction === 'supplier_id'
                                            ? [...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')).map(s => (
                                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                            ))
                                            : (bulkAction === 'category' ? categories : familles).map(v => (
                                                <SelectItem key={v} value={v}>{v}</SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            )}
                            {bulkAction && bulkValue && (
                                <Button size="sm" onClick={handleBulkApply} data-testid="bulk-apply-btn">
                                    Appliquer
                                </Button>
                            )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedProducts(new Set()); setSelectedSupplierProducts(new Set()); setBulkAction(null); setBulkValue(''); }} className="ml-auto text-slate-500">
                            Annuler
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Products List - Scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto mt-4 space-y-1.5">
                {filteredProducts.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                            <p className="text-slate-500">Aucun produit trouvé</p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                    {editMode && (
                    <div className="flex items-center gap-2 px-2 pb-1">
                        <input
                            type="checkbox"
                            checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-slate-300 accent-orange-500"
                            data-testid="select-all-checkbox"
                        />
                        <span className="text-xs text-slate-500">Tout sélectionner ({filteredProducts.length})</span>
                    </div>
                    )}
                    {(() => {
                        let currentPrimary = null;
                        let currentSecondary = null;
                        return filteredProducts.map((product) => {
                            const productSuppliers = getSupplierProductsForProduct(product.id);
                            const isExpanded = expandedProduct === product.id;
                            const primaryValue = (groupBy === 'famille' ? product.famille : product.category) || '';
                            const secondaryValue = (groupBy === 'famille' ? product.category : product.famille) || '';
                            const showPrimaryHeader = primaryValue !== currentPrimary;
                            const showSecondaryHeader = showPrimaryHeader || secondaryValue !== currentSecondary;
                            currentPrimary = primaryValue;
                            currentSecondary = secondaryValue;

                            const primaryFallback = groupBy === 'famille' ? 'Sans famille' : 'Sans catégorie';
                            const secondaryFallback = groupBy === 'famille' ? 'Sans catégorie' : 'Sans famille';

                            return (
                                <div key={product.id}>
                                    {showPrimaryHeader && (
                                        <div className="flex items-center gap-3 py-3 mt-4 first:mt-0" data-testid={`group-header-${primaryValue || 'sans-groupe'}`}>
                                            <div className="h-px flex-1 bg-slate-200"></div>
                                            <span className="text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                                                {primaryValue || primaryFallback}
                                            </span>
                                            <div className="h-px flex-1 bg-slate-200"></div>
                                        </div>
                                    )}
                                    {showSecondaryHeader && (
                                        <div className="flex items-center gap-2 pl-2 mt-2 mb-1" data-testid={`subgroup-header-${secondaryValue || 'sans-sous-groupe'}`}>
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                                                {secondaryValue || secondaryFallback}
                                            </span>
                                            <div className="h-px flex-1 bg-slate-100"></div>
                                        </div>
                                    )}
                                    <Card className={`overflow-hidden ${editMode && selectedProducts.has(product.id) ? 'ring-1 ring-orange-400 bg-orange-50/30' : ''}`} data-testid={`product-card-${product.id}`}>
                                        <div 
                                            className="px-3 py-2 flex items-center justify-between gap-2 cursor-pointer hover:bg-slate-50 transition-colors"
                                            onClick={() => {
                                                if (isExpanded) closeExpandedProduct();
                                                else setExpandedProduct(product.id);
                                            }}
                                        >
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                {editMode && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProducts.has(product.id)}
                                                    onChange={(e) => toggleProductSelection(product.id, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-4 h-4 rounded border-slate-300 accent-orange-500 flex-shrink-0"
                                                    data-testid={`select-product-${product.id}`}
                                                />
                                                )}
                                                <button className="p-1 hover:bg-slate-200 rounded">
                                                    {isExpanded ? (
                                                        <ChevronDown className="w-4 h-4 text-slate-500" />
                                                    ) : (
                                                        <ChevronRight className="w-4 h-4 text-slate-500" />
                                                    )}
                                                </button>
                                                {product.image_url ? (
                                                    <img 
                                                        src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`} 
                                                        alt="" 
                                                        className={`rounded-md object-contain border border-slate-200 transition-all ${isExpanded ? 'max-w-28 max-h-36' : 'max-w-16 max-h-16'}`}
                                                    />
                                                ) : (
                                                    <div className={`rounded-md bg-slate-100 flex items-center justify-center ${isExpanded ? 'w-28 h-28' : 'w-16 h-16'}`}>
                                                        <Package className={`text-slate-300 ${isExpanded ? 'w-12 h-12' : 'w-8 h-8'}`} />
                                                    </div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-slate-900 text-sm truncate">{product.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-slate-500">
                                                        <span>Unité: {product.unit}</span>
                                                        <span className="flex items-center gap-1">
                                                            <LinkIcon className="w-3 h-3" />
                                                            {productSuppliers.length} fournisseur(s)
                                                        </span>
                                                    </div>
                                                    {product.allergens && product.allergens.length > 0 && (
                                                        <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                                            {product.allergens.map((a) => (
                                                                <span key={a} className="px-1 py-0 bg-orange-100 text-orange-600 text-[9px] font-medium rounded">
                                                                    {a}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                        {editMode && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => handleOpenProductDialog(product)}
                                                    data-testid={`edit-product-${product.id}`}
                                                    disabled={!canEdit}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {canDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDeleteClick(product, 'product')}
                                                        data-testid={`delete-product-${product.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {isExpanded && (
                                    <div className="border-t border-slate-200 bg-slate-50 p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="font-semibold text-slate-700">Produits fournisseurs</h4>
                                            {canCreateSupplierProduct && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleOpenSupplierProductDialog(product)}
                                                data-testid={`add-supplier-product-${product.id}`}
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Ajouter un fournisseur
                                            </Button>
                                            )}
                                        </div>
                                        
                                        {productSuppliers.length === 0 ? (
                                            <p className="text-slate-500 text-sm">Aucun fournisseur associé</p>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="text-left text-slate-500 border-b border-slate-200">
                                                            <th className="pb-2 w-10"></th>
                                                            <th className="pb-2 font-medium">Fournisseur</th>
                                                            <th className="pb-2 font-medium">Référence</th>
                                                            <th className="pb-2 font-medium">Désignation</th>
                                                            <th className="pb-2 font-medium">Colisage</th>
                                                            <th className="pb-2 font-medium">Cond. vente</th>
                                                            <th className="pb-2 font-medium">Unité fact.</th>
                                                            <th className="pb-2 font-medium">Contenance</th>
                                                            <th className="pb-2 font-medium text-right">Prix unitaire</th>
                                                            <th className="pb-2 font-medium text-right">Tarif/u.cont.</th>
                                                            {!editMode && <th className="pb-2 font-medium text-center">Évolution</th>}
                                                            <th className="pb-2"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {productSuppliers.map((sp) => {
                                                            // Colisage complet (ex: "carton de 5 sachets")
                                                            let colisageDisplay = '-';
                                                            if (sp.packaging_qty && sp.order_unit && sp.billing_unit) {
                                                                colisageDisplay = `${sp.order_unit} de ${sp.packaging_qty} ${sp.billing_unit}`;
                                                            }
                                                            
                                                            // Build conditionnement de vente display
                                                            let conditionnement = '-';
                                                            if (sp.sell_by_detail) {
                                                                // Vente au détail : l'unité de vente est l'unité de colisage
                                                                conditionnement = sp.billing_unit || 'Pièce';
                                                            } else if (sp.packaging_qty && sp.order_unit && sp.billing_unit) {
                                                                conditionnement = `${sp.order_unit} de ${sp.packaging_qty} ${sp.billing_unit}`;
                                                            } else if (sp.order_unit) {
                                                                conditionnement = sp.order_unit;
                                                            }
                                                            
                                                            // Contenance display
                                                            const contenance = sp.capacity_value && sp.capacity_unit 
                                                                ? `${sp.capacity_value} ${sp.capacity_unit}` 
                                                                : '-';
                                                            
                                                            // Invoice unit display (use invoice_unit if available, fallback to billing_unit)
                                                            const invoiceUnit = sp.invoice_unit || sp.billing_unit || '-';
                                                            
                                                            // Price display with invoice unit
                                                            const priceDisplay = sp.unit_price 
                                                                ? `${formatCurrency(sp.unit_price)}/${sp.invoice_unit || sp.billing_unit || 'unité'}`
                                                                : '-';
                                                            
                                                            // Tarif / Contenance calculation
                                                            const tarifContenance = sp.unit_price && sp.capacity_value && sp.capacity_value > 0
                                                                ? `${formatCurrency(sp.unit_price / sp.capacity_value)}/${sp.capacity_unit || 'u'}`
                                                                : '-';
                                                            
                                                            return (
                                                                <tr key={sp.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors ${sp.commandable === false ? 'opacity-60' : ''} ${editMode && selectedSupplierProducts.has(sp.id) ? 'bg-orange-50' : ''}`} onClick={() => setSpDetailProduct(sp)} data-testid={`sp-row-${sp.id}`}>
                                                                    <td className={`py-2 ${editMode ? 'w-16' : 'w-10'}`}>
                                                                        <div className="flex items-center gap-1.5">
                                                                            {editMode && (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={selectedSupplierProducts.has(sp.id)}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    onChange={(e) => toggleSupplierProductSelection(sp.id, e)}
                                                                                    className="w-4 h-4 cursor-pointer accent-orange-500 flex-shrink-0"
                                                                                    data-testid={`sp-checkbox-${sp.id}`}
                                                                                />
                                                                            )}
                                                                            <div className="relative">
                                                                                {sp.image_url ? (
                                                                                    <div className="w-8 h-8 rounded overflow-hidden border border-slate-200 bg-white flex-shrink-0">
                                                                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${sp.image_url}`} alt="" className="w-full h-full object-contain" />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center flex-shrink-0">
                                                                                        <Package className="w-4 h-4 text-slate-300" />
                                                                                    </div>
                                                                                )}
                                                                                {sp.commandable === false && (
                                                                                    <div className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-red-100 flex items-center justify-center" title="Non commandable">
                                                                                        <ShoppingCart className="w-2.5 h-2.5 text-red-400 line-through" style={{ textDecoration: 'line-through' }} />
                                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                                            <div className="w-3.5 h-[1.5px] bg-red-400 rotate-45 rounded-full"></div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                {sp.variable_weight && (
                                                                                    <div
                                                                                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center"
                                                                                        title="Poids variable"
                                                                                        data-testid={`sp-variable-weight-${sp.id}`}
                                                                                    >
                                                                                        <Scale className="w-2.5 h-2.5 text-amber-600" />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-2 font-medium">{sp.supplier_name}</td>
                                                                    <td className="py-2 font-mono text-xs">{sp.supplier_ref}</td>
                                                                    <td className="py-2">{sp.supplier_name_product}</td>
                                                                    <td className="py-2">
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                                                                            {colisageDisplay}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-2">
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">
                                                                            {conditionnement}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-2">
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                                                                            {invoiceUnit}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-2 text-slate-600">{contenance}</td>
                                                                    <td className="py-2 text-right font-medium">{priceDisplay}</td>
                                                                    <td className="py-2 text-right font-semibold text-emerald-600">{tarifContenance}</td>
                                                                    {!editMode && (
                                                                        <td className="py-2" onClick={(e) => e.stopPropagation()}>
                                                                            <div className="flex justify-center">
                                                                                <PriceSparkline data={priceSparklines[sp.id]} />
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                    <td className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                                                        {editMode && (
                                                                        <>
                                                                        {/* Compare toggle — placed alongside the other edit-mode
                                                                            action icons so the row stays clean outside edit mode. */}
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className={sp.compare ? "text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50" : "text-slate-300 hover:text-indigo-500 hover:bg-indigo-50"}
                                                                            onClick={() => handleToggleCompare(sp)}
                                                                            data-testid={`toggle-compare-${sp.id}`}
                                                                            title={sp.compare ? "Retirer du comparatif" : "Ajouter au comparatif"}
                                                                        >
                                                                            <BarChart3 className="w-3 h-3" />
                                                                        </Button>
                                                                        {canToggleCommandable && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className={sp.commandable !== false ? "text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50" : "text-slate-300 hover:text-slate-600 hover:bg-slate-50"}
                                                                            onClick={() => handleToggleCommandable(sp)}
                                                                            data-testid={`toggle-commandable-${sp.id}`}
                                                                            title={sp.commandable !== false ? "Commandable — cliquer pour désactiver" : "Non commandable — cliquer pour réactiver"}
                                                                        >
                                                                            <ShoppingCart className="w-3 h-3" />
                                                                        </Button>
                                                                        )}
                                                                        {canEditSupplierProduct && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleOpenSupplierProductDialog(product, sp)}
                                                                            data-testid={`edit-supplier-product-${sp.id}`}
                                                                        >
                                                                            <Pencil className="w-3 h-3" />
                                                                        </Button>
                                                                        )}
                                                                        {canEditSupplierProduct && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                                                            onClick={() => setMoveSpDialog({ open: true, supplierProduct: sp, currentName: product.name })}
                                                                            data-testid={`move-supplier-product-${sp.id}`}
                                                                            title="Changer de produit industriel"
                                                                        >
                                                                            <ArrowRightLeft className="w-3 h-3" />
                                                                        </Button>
                                                                        )}
                                                                        {canDeleteSupplierProduct && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="text-red-500"
                                                                            onClick={() => handleDeleteClick(sp, 'supplier_product')}
                                                                            data-testid={`delete-supplier-product-${sp.id}`}
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                        </Button>
                                                                        )}
                                                                        </>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        </div>
                    );
                        })
                    })()
                    }
                    </>
                )}
            </div>

            <ProductsDialogs
                products={products} suppliers={suppliers}
                categories={categories} setCategories={setCategories}
                familles={familles} setFamilles={setFamilles}
                productDialog={productDialog} setProductDialog={setProductDialog}
                editingProduct={editingProduct}
                productForm={productForm} setProductForm={setProductForm}
                showNewCategoryInput={showNewCategoryInput} setShowNewCategoryInput={setShowNewCategoryInput}
                newCategoryName={newCategoryName} setNewCategoryName={setNewCategoryName}
                showNewFamilleInput={showNewFamilleInput} setShowNewFamilleInput={setShowNewFamilleInput}
                newFamilleName={newFamilleName} setNewFamilleName={setNewFamilleName}
                famillePopoverOpen={famillePopoverOpen} setFamillePopoverOpen={setFamillePopoverOpen}
                hideUnselectedAllergens={hideUnselectedAllergens} setHideUnselectedAllergens={setHideUnselectedAllergens}
                imagePreview={imagePreview} imageFile={imageFile}
                showCropDialog={showCropDialog} setShowCropDialog={setShowCropDialog}
                cropImageSrc={cropImageSrc}
                handleImageSelect={handleImageSelect} handleCropDone={handleCropDone} handleImageRemove={handleImageRemove}
                showImagePicker={showImagePicker} setShowImagePicker={setShowImagePicker}
                handlePickExistingImage={handlePickExistingImage}
                handleSaveProduct={handleSaveProduct}
                supplierProductDialog={supplierProductDialog} setSupplierProductDialog={setSupplierProductDialog}
                editingSupplierProduct={editingSupplierProduct}
                supplierProductForm={supplierProductForm} setSupplierProductForm={setSupplierProductForm}
                spImagePreview={spImagePreview} spImageFile={spImageFile}
                spShowCropDialog={spShowCropDialog} setSpShowCropDialog={setSpShowCropDialog}
                spCropImageSrc={spCropImageSrc} setSpCropImageSrc={setSpCropImageSrc}
                setSpImagePreview={setSpImagePreview} setSpImageFile={setSpImageFile} setSpImageDeleted={setSpImageDeleted}
                setSpImageSourceRef={setSpImageSourceRef}
                showSpImagePicker={showSpImagePicker} setShowSpImagePicker={setShowSpImagePicker}
                handleSpPickExistingImage={handleSpPickExistingImage}
                spImageHeight={spImageHeight} setSpImageHeight={setSpImageHeight}
                spLoadingImage={spLoadingImage} setSpLoadingImage={setSpLoadingImage}
                handleSaveSupplierProduct={handleSaveSupplierProduct}
                orderUnits={orderUnits} billingUnits={billingUnits} invoiceUnits={invoiceUnits}
                newOrderUnit={newOrderUnit} setNewOrderUnit={setNewOrderUnit}
                newBillingUnit={newBillingUnit} setNewBillingUnit={setNewBillingUnit}
                newInvoiceUnit={newInvoiceUnit} setNewInvoiceUnit={setNewInvoiceUnit}
                orderUnitPopoverOpen={orderUnitPopoverOpen} setOrderUnitPopoverOpen={setOrderUnitPopoverOpen}
                billingUnitPopoverOpen={billingUnitPopoverOpen} setBillingUnitPopoverOpen={setBillingUnitPopoverOpen}
                invoiceUnitPopoverOpen={invoiceUnitPopoverOpen} setInvoiceUnitPopoverOpen={setInvoiceUnitPopoverOpen}
                handleAddOrderUnit={handleAddOrderUnit} handleRemoveOrderUnit={handleRemoveOrderUnit}
                handleAddBillingUnit={handleAddBillingUnit} handleRemoveBillingUnit={handleRemoveBillingUnit}
                handleAddInvoiceUnit={handleAddInvoiceUnit} handleRemoveInvoiceUnit={handleRemoveInvoiceUnit}
                deleteDialog={deleteDialog} setDeleteDialog={setDeleteDialog}
                deleteType={deleteType}
                handleConfirmDelete={handleConfirmDelete}
                spDetailProduct={spDetailProduct} setSpDetailProduct={setSpDetailProduct}
                canToggleCommandable={canToggleCommandable}
            />

            <MoveSupplierProductDialog
                open={moveSpDialog.open}
                onClose={() => setMoveSpDialog({ open: false, supplierProduct: null, currentName: '' })}
                supplierProduct={moveSpDialog.supplierProduct}
                currentIndustrialProductName={moveSpDialog.currentName}
                industrialProducts={products}
                onMoved={(newId) => {
                    // Optimistic local update so the UI reflects the change immediately
                    setSupplierProducts(prev => prev.map(sp =>
                        sp.id === moveSpDialog.supplierProduct?.id ? { ...sp, industrial_product_id: newId } : sp
                    ));
                }}
            />

            <ProductPhotoAssistantDialog
                open={photoAssistantOpen}
                onClose={() => setPhotoAssistantOpen(false)}
                suppliers={suppliers}
                categories={categories}
                onCreated={() => loadProducts()}
            />
        </div>
    );
}
